'use client';

import type NextLink from 'next/link';
import type { ComponentProps, ReactNode } from 'react';

import { isMarketingPath, isPlatformPath, resolveCrossAppHref } from '@naumu/shared/routes/origins';
import { UiLinkProvider, type UiLinkComponent, type UiLinkProps } from '@naumu/ui/lib/ui-link';

/**
 * The one cross-app link implementation, parameterised by which app is asking.
 *
 * Since the Phase 3 split, `apps/web` (platform) and `apps/marketing` are
 * two containers behind ONE hostname. A route that belongs to the other
 * container is absent from this app's route manifest, so `next/link` cannot
 * soft-navigate to it: all it can do is fire a prefetch and an RSC request at
 * the other container and then fall back to a document navigation. (That
 * fallback exists only because the two apps get different random Next build
 * IDs; never pin `generateBuildId` across them - see
 * `@naumu/shared/routes/origins`.) A plain `<a>` IS the document navigation,
 * with no wasted round trips.
 *
 * Both apps needed exactly that rule with the predicate inverted, in two
 * places each (a direct-import `AppLink` and the `UiLink` bridge that applies
 * the same rule to every DS component rendering a link). Four near-identical
 * files is the drift this factory removes: pass `side`, get both.
 *
 * `Link` is injected rather than imported so this package never pulls
 * `next/link` into module scope - platform-client is consumed by two Next apps
 * that each own their router, and the type-only import above keeps the
 * signature exact without the runtime edge.
 */
export type AppSide = 'platform' | 'marketing';

type NextLinkProps = ComponentProps<typeof NextLink>;

type AppLinkProps = Omit<NextLinkProps, 'href'> & {
	href: string;
	children?: ReactNode;
};

export interface CreateAppLinkOptions {
	/** Which container this app IS. Hrefs belonging to the other one go through an anchor. */
	side: AppSide;
	/** The host app's `next/link`. */
	Link: typeof NextLink;
	/**
	 * Same-container hrefs that must still be a DOCUMENT navigation (a plain
	 * anchor, no prefetch, no soft route): the platform binds the credential
	 * routes (`/join/:token`, `/h/:code?t=`) so a Google container or replay
	 * session started elsewhere in the SPA never sees a tokenised URL.
	 * Default: none, every same-container href soft-navigates as before.
	 */
	hardNavigate?: (href: string) => boolean;
}

export interface AppLinkKit {
	/** Drop-in replacement for `next/link` on any surface that can emit a foreign href. */
	AppLink: (props: AppLinkProps) => ReactNode;
	/** Teaches `@naumu/ui` how to render a link from this app (the `UiLink` seam). */
	NextLinkBridge: (props: { children: ReactNode }) => ReactNode;
}

export const createAppLink = ({ side, Link, hardNavigate }: CreateAppLinkOptions): AppLinkKit => {
	/** True when `href` is served by the OTHER container. */
	const isForeign = side === 'platform' ? isMarketingPath : isPlatformPath;
	/** Same container, but a full document load on purpose (credential routes). */
	const isHard = (href: string): boolean => !!hardNavigate && !isForeign(href) && hardNavigate(href);

	const AppLink = ({ href, children, ...rest }: AppLinkProps) => {
		if (isForeign(href) || isHard(href)) {
			const { prefetch: _prefetch, replace: _replace, scroll: _scroll, ...anchorProps } = rest;
			return (
				<a href={isForeign(href) ? resolveCrossAppHref(href, side) : href} {...(anchorProps as ComponentProps<'a'>)}>
					{children}
				</a>
			);
		}
		return (
			<Link href={href} {...rest}>
				{children}
			</Link>
		);
	};

	const BridgeUiLink: UiLinkComponent = ({ href, children, ...rest }: UiLinkProps) => {
		if (isForeign(href) || isHard(href)) {
			return (
				<a href={isForeign(href) ? resolveCrossAppHref(href, side) : href} {...rest}>
					{children}
				</a>
			);
		}
		return (
			<Link href={href} {...rest}>
				{children}
			</Link>
		);
	};

	const NextLinkBridge = ({ children }: { children: ReactNode }) => (
		<UiLinkProvider component={BridgeUiLink}>{children}</UiLinkProvider>
	);

	return { AppLink, NextLinkBridge };
};
